import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  Button,
  Switch,
  Label,
  Input,
  Icon,
  Card,
  CardHeader,
  CardBody,
  showToast,
  Select,
  type SelectOption,
} from '@repo/ui';
import { useTenantConfig } from '../queries/useTenantQuery';
import { useReportingConfigMutation } from '../queries/useReportingQuery';
import { useTemplates } from '../queries/useTemplateQuery';
import type { ReportingModuleConfig } from '@xtnd/web/shared/types/tenantSettingTypes';

interface ReportingFormData {
  enabled: boolean;
  arExceptionReporting: {
    enabled: boolean;
  };
  trustReconciliationReporting: {
    enabled: boolean;
    clioTrustBankAccountId?: string;
    qboTrustBankAccountId?: string;
    qboTrustLiabilityAccountId?: string;
  };
  matterLedgerReporting: {
    enabled: boolean;
    pdfTemplateId?: string;
  };
  matterStatementReporting: {
    enabled: boolean;
    pdfTemplateId?: string;
  };
}

export default function ReportingConfigPage() {
  const navigate = useNavigate();
  const { data: tenantConfig, isLoading } = useTenantConfig();
  const reportingConfigMutation = useReportingConfigMutation();

  // Fetch available templates
  const { data: templates = [] } = useTemplates();

  // Filter to only PDF templates
  const pdfTemplates = useMemo(() => templates.filter(t => t.type === 'pdf'), [templates]);

  // Convert PDF templates to SelectOption format
  const pdfTemplateOptions: SelectOption[] = useMemo(() => {
    return pdfTemplates.map(template => ({
      value: template.id,
      label: template.name,
    }));
  }, [pdfTemplates]);

  const [formData, setFormData] = useState<ReportingFormData>({
    enabled: true,
    arExceptionReporting: {
      enabled: true,
    },
    trustReconciliationReporting: {
      enabled: false,
      clioTrustBankAccountId: '',
      qboTrustBankAccountId: '',
      qboTrustLiabilityAccountId: '',
    },
    matterLedgerReporting: {
      enabled: false,
      pdfTemplateId: '',
    },
    matterStatementReporting: {
      enabled: false,
      pdfTemplateId: '',
    },
  });

  const [errors, setErrors] = useState<{
    clioTrustBankAccountId?: string;
    qboTrustBankAccountId?: string;
    qboTrustLiabilityAccountId?: string;
  }>({});

  useEffect(() => {
    if (tenantConfig?.modules?.reporting) {
      const reporting = tenantConfig.modules.reporting as ReportingModuleConfig;
      setFormData({
        enabled: reporting.enabled ?? true,
        arExceptionReporting: {
          enabled: reporting.arExceptionReporting?.enabled ?? true,
        },
        trustReconciliationReporting: {
          enabled: reporting.trustReconciliationReporting?.enabled ?? false,
          clioTrustBankAccountId:
            reporting.trustReconciliationReporting?.clioTrustBankAccountId ?? '',
          qboTrustBankAccountId:
            reporting.trustReconciliationReporting?.qboTrustBankAccountId ?? '',
          qboTrustLiabilityAccountId:
            reporting.trustReconciliationReporting?.qboTrustLiabilityAccountId ?? '',
        },
        matterLedgerReporting: {
          enabled: reporting.matterLedgerReporting?.enabled ?? false,
          pdfTemplateId: reporting.matterLedgerReporting?.pdfTemplateId ?? '',
        },
        matterStatementReporting: {
          enabled: reporting.matterStatementReporting?.enabled ?? false,
          pdfTemplateId: reporting.matterStatementReporting?.pdfTemplateId ?? '',
        },
      });
    }
  }, [tenantConfig]);

  const validateForm = (): boolean => {
    const newErrors: {
      clioTrustBankAccountId?: string;
      qboTrustBankAccountId?: string;
      qboTrustLiabilityAccountId?: string;
    } = {};

    if (formData.trustReconciliationReporting.enabled && formData.enabled) {
      if (!formData.trustReconciliationReporting.clioTrustBankAccountId?.trim()) {
        newErrors.clioTrustBankAccountId = 'Clio Trust Bank Account ID is required';
      }
      if (!formData.trustReconciliationReporting.qboTrustBankAccountId?.trim()) {
        newErrors.qboTrustBankAccountId = 'QBO Trust Bank Account ID is required';
      }
      if (!formData.trustReconciliationReporting.qboTrustLiabilityAccountId?.trim()) {
        newErrors.qboTrustLiabilityAccountId = 'QBO Trust Liability Account ID is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showToast.error('Please fix validation errors before saving');
      return;
    }

    try {
      await reportingConfigMutation.mutateAsync(formData);
      // Toast success message is handled in mutation hook
    } catch (error) {
      // Error toast is handled in mutation hook
      console.error('Failed to save reporting configuration:', error);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <PageLayout
      title="Reporting Configuration"
      description="Configure reporting features and account settings"
      icon="HiDocumentText"
      isLoading={isLoading}
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSave();
        }}
        className="space-y-8"
      >
        {/* Master Enable Toggle */}
        <div className="bg-white dark:bg-xtnd-dark-800 rounded-lg border border-gray-200 dark:border-xtnd-dark-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-blue" />
              </div>
              <div>
                <Label
                  htmlFor="reporting-enabled"
                  className="text-base font-semibold text-xtnd-dark dark:text-xtnd-white"
                >
                  Enable Reporting Module
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Master switch for all reporting features
                </p>
              </div>
            </div>
            <Switch
              id="reporting-enabled"
              checked={formData.enabled}
              onChange={checked => setFormData({ ...formData, enabled: checked })}
              disabled={isLoading || reportingConfigMutation.isPending}
            />
          </div>
        </div>

        {/* Reporting Features Grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${
            !formData.enabled ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          {/* AR Exception Reporting Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Icon name="HiInformationCircle" className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-Sdark dark:text-xtnd-white">
                    AR Exception Reporting
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Accounts receivable exception reporting
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="ar-exception-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable AR Exception Reporting
                  </Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Track and report accounts receivable exceptions
                  </p>
                </div>
                <Switch
                  id="ar-exception-enabled"
                  checked={formData.arExceptionReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      arExceptionReporting: {
                        ...formData.arExceptionReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>
            </CardBody>
          </Card>

          {/* Matter Ledger Report Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                  <Icon name="HiTable" className="h-5 w-5 text-xtnd-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Matter Ledger Report
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Detailed transaction history for matters
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="matter-ledger-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable Matter Ledger Report
                  </Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    View detailed transaction history for specific matters
                  </p>
                </div>
                <Switch
                  id="matter-ledger-enabled"
                  checked={formData.matterLedgerReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      matterLedgerReporting: {
                        ...formData.matterLedgerReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>

              {/* PDF Template Selection */}
              {formData.matterLedgerReporting.enabled && formData.enabled && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-xtnd-dark-600">
                  <Select<SelectOption>
                    label="PDF Template"
                    value={
                      formData.matterLedgerReporting.pdfTemplateId
                        ? pdfTemplateOptions.find(
                            opt => opt.value === formData.matterLedgerReporting.pdfTemplateId
                          ) || null
                        : null
                    }
                    onChange={option => {
                      setFormData({
                        ...formData,
                        matterLedgerReporting: {
                          ...formData.matterLedgerReporting,
                          pdfTemplateId: option?.value || '',
                        },
                      });
                    }}
                    options={pdfTemplateOptions}
                    placeholder="Select PDF template..."
                    helperText="Select PDF template for Matter Ledger Report exports"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    isLoading={isLoading || reportingConfigMutation.isPending}
                    fullWidth={true}
                  />
                </div>
              )}
            </CardBody>
          </Card>

          {/* Matter Statement Report Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                  <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Matter Statement Report
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Comprehensive account statements for matters
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="matter-statement-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable Matter Statement Report
                  </Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Generate comprehensive account statements for specific matters
                  </p>
                </div>
                <Switch
                  id="matter-statement-enabled"
                  checked={formData.matterStatementReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      matterStatementReporting: {
                        ...formData.matterStatementReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>

              {/* PDF Template Selection */}
              {formData.matterStatementReporting.enabled && formData.enabled && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-xtnd-dark-600">
                  <Select<SelectOption>
                    label="PDF Template"
                    value={
                      formData.matterStatementReporting.pdfTemplateId
                        ? pdfTemplateOptions.find(
                            opt => opt.value === formData.matterStatementReporting.pdfTemplateId
                          ) || null
                        : null
                    }
                    onChange={option => {
                      setFormData({
                        ...formData,
                        matterStatementReporting: {
                          ...formData.matterStatementReporting,
                          pdfTemplateId: option?.value || '',
                        },
                      });
                    }}
                    options={pdfTemplateOptions}
                    placeholder="Select PDF template..."
                    helperText="Select PDF template for Matter Statement Report exports"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    isLoading={isLoading || reportingConfigMutation.isPending}
                    fullWidth={true}
                  />
                </div>
              )}
            </CardBody>
          </Card>

          {/* Trust Reconciliation Reporting Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                  <Icon name="HiShieldCheck" className="h-5 w-5 text-xtnd-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    3-Way Trust Reconciliation
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Trust account reconciliation reporting
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="trust-reconciliation-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable Trust Reconciliation
                  </Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Verify Clio Trust transactions match QBO accounts
                  </p>
                </div>
                <Switch
                  id="trust-reconciliation-enabled"
                  checked={formData.trustReconciliationReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      trustReconciliationReporting: {
                        ...formData.trustReconciliationReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>

              {/* Account Configuration */}
              {formData.trustReconciliationReporting.enabled && formData.enabled && (
                <div className="space-y-4 mt-4 pt-4 border-t border-gray-200 dark:border-xtnd-dark-600">
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white mb-1">
                      Account Configuration
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Configure account IDs for reconciliation
                    </p>
                  </div>

                  <Input
                    label="Clio Trust Bank Account ID"
                    id="clio-trust-bank-account-id"
                    value={formData.trustReconciliationReporting.clioTrustBankAccountId || ''}
                    onChange={e => {
                      setFormData({
                        ...formData,
                        trustReconciliationReporting: {
                          ...formData.trustReconciliationReporting,
                          clioTrustBankAccountId: e.target.value,
                        },
                      });
                      if (errors.clioTrustBankAccountId) {
                        setErrors({ ...errors, clioTrustBankAccountId: undefined });
                      }
                    }}
                    error={errors.clioTrustBankAccountId}
                    required
                    helperText="Clio Bank Account ID for filtering Trust transactions"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    fullWidth
                  />

                  <Input
                    label="QBO Trust Bank Account ID"
                    id="qbo-trust-bank-account-id"
                    value={formData.trustReconciliationReporting.qboTrustBankAccountId || ''}
                    onChange={e => {
                      setFormData({
                        ...formData,
                        trustReconciliationReporting: {
                          ...formData.trustReconciliationReporting,
                          qboTrustBankAccountId: e.target.value,
                        },
                      });
                      if (errors.qboTrustBankAccountId) {
                        setErrors({ ...errors, qboTrustBankAccountId: undefined });
                      }
                    }}
                    error={errors.qboTrustBankAccountId}
                    required
                    helperText="QuickBooks Online Account ID for Trust Bank Account"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    fullWidth
                  />

                  <Input
                    label="QBO Trust Liability Account ID"
                    id="qbo-trust-liability-account-id"
                    value={formData.trustReconciliationReporting.qboTrustLiabilityAccountId || ''}
                    onChange={e => {
                      setFormData({
                        ...formData,
                        trustReconciliationReporting: {
                          ...formData.trustReconciliationReporting,
                          qboTrustLiabilityAccountId: e.target.value,
                        },
                      });
                      if (errors.qboTrustLiabilityAccountId) {
                        setErrors({ ...errors, qboTrustLiabilityAccountId: undefined });
                      }
                    }}
                    error={errors.qboTrustLiabilityAccountId}
                    required
                    helperText="QuickBooks Online Account ID for Trust Liability Account"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    fullWidth
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Form Footer with Save Button */}
        <div className="sticky bottom-0 bg-white dark:bg-xtnd-dark-800 border-t border-gray-200 dark:border-xtnd-dark-600 -mx-8 px-8 py-4 mt-8">
          <div className="flex items-center justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={reportingConfigMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={reportingConfigMutation.isPending}
              disabled={isLoading || Object.keys(errors).length > 0}
            >
              Save All Settings
            </Button>
          </div>
        </div>
      </form>
    </PageLayout>
  );
}
